import Axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueries, useQuery, useQueryClient, UseQueryResult } from "react-query";
import { caseKeys, poiKeys } from "src/query-keys";
import { APIError, PoiTimeseries, PoiTimeseriesType, Results } from "src/types";
import { parseServerError } from "src/utils/string.utils";

export interface ResultDownloadResponse {
  downloadUrl: string;
}

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: Results) {
  return data;
}

export function useStartCalculationMutation(caseId: string | number) {
  const queryClient = useQueryClient();

  function mutationFn(caseId: string | number): Promise<void> {
    return Axios.post(`/planning-api/cases/${caseId}/calculation`, {
      id: 0, // necessary for backend to work
    });
  }

  return useMutation<void, AxiosError<APIError[]>>(() => mutationFn(caseId), {
    onSettled: () => {
      queryClient.invalidateQueries(caseKeys.detail(caseId));
    },
  });
}

export function useCancelCalculationMutation(caseId: string | number) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  async function mutationFn(caseId: string | number): Promise<void> {
    await Axios.delete(`/planning-api/cases/${caseId}/calculation`);
  }

  return useMutation<void, AxiosError<APIError[]>>(() => mutationFn(caseId), {
    onError: (error) => {
      const data = error.response?.data;
      const serverError = Array.isArray(data) ? data[0] : undefined;
      const message =
        serverError && typeof serverError.message === "string" && Array.isArray(serverError.arguments)
          ? parseServerError(serverError)
          : t("label.cancelCalculationFailed", "Could not cancel the calculation. Please try again.");

      enqueueSnackbar(message, { variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries(caseKeys.all, { exact: true });
      return queryClient.invalidateQueries(caseKeys.detail(caseId));
    },
  });
}

export function useCalculationResults(caseId: string | number) {
  async function queryFn(id: string | number): Promise<Results> {
    const response = await Axios.get(`/planning-api/cases/${id}/results`);
    return response.data.map(deserialize);
  }

  return useQuery<Results, Error>(caseKeys.result(caseId), () => {
    return queryFn(caseId);
  });
}

export function useCaseResultDownload(caseId: string | number) {
  async function queryFn(caseId: string | number) {
    const response = await Axios.get<ResultDownloadResponse>(`/planning-api/cases/${caseId}/result/download`);
    return response.data;
  }

  return useQuery<ResultDownloadResponse, Error>(caseKeys.resultDownload(caseId), () => queryFn(caseId));
}

export function usePoiTimeseries(
  scenarioIds: (string | number)[],
  poiId: string | number,
  substanceId: string | number,
  type: PoiTimeseriesType
) {
  async function queryFn(
    scenarioId: string | number,
    poiId: string | number,
    substanceId: string | number,
    type: PoiTimeseriesType
  ): Promise<PoiTimeseries> {
    const response = await Axios.get(`/planning-api/scenarios/${scenarioId}/pois/${poiId}/timeseries`, {
      params: { substanceId, type },
    });
    return response.data;
  }

  const queryResults = useQueries(
    scenarioIds.map((scenarioId) => {
      return {
        queryKey: poiKeys.timeseries(scenarioId, poiId, substanceId, type),
        queryFn: () => {
          return queryFn(scenarioId, poiId, substanceId, type);
        },
      };
    })
  ) as UseQueryResult<PoiTimeseries>[];

  return queryResults;
}
